import { TopBarMobile } from "@/components/topBarMobile/topBarMobile";
import { translate, TranslatorKeys } from "@/config/translator";
import {
  AttendanceMobileEmployeeScreenTab,
  useAttendanceMobileEmployeeScreen,
} from "./attendanceMobileEmployeeScreen/useAttendanceMobileEmployeeScreen";
import { clsx } from "clsx/lite";
// eslint-disable-next-line max-len
import { AttendancesMobileHeader } from "@/components/attendancesMobileHeader/attendancesMobileHeader";
import { FullScreenSpinner } from "@/components/fullScreenSpinner/FullScreenSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowTurnLeft,
  faPencil,
  faPlus,
} from "@fortawesome/pro-regular-svg-icons";
import { MobileBottomSheet } from "@/components/mobileBottomSheet/mobileBottomSheet";
import { PspTextArea } from "@/components/pspTextArea/pspTextArea";
import PspButton from "@/components/pspButton/PspButton";
import { Tab, TabList } from "@fluentui/react-components";
// eslint-disable-next-line max-len
import { AttendanceMobileEmployeeHoursTab } from "./attendanceMobileEmployeeScreen/attendanceMobileEmployeeHoursTab";
import { PressableDashedBox } from "@/components/pressableDashedBox/pressableDashedBox";
// eslint-disable-next-line max-len
import { AttendanceMobileEmployeeVociTab } from "./attendanceMobileEmployeeScreen/attendanceMobileEmployeeVociTab";
import ConfirmationDialog from "@/components/confirmationDialog/ConfirmationDialog";

export function AttendanceMobileEmployeeScreen() {
  const {
    lang,
    attendance,
    month,
    year,
    editNoteBottomSheetOpen,
    closeNoteBottomSheet,
    note,
    setNote,
    onSaveNote,
    noteIsClamped,
    noteRef,
    noteIsExpanded,
    setNoteIsExpanded,
    openNoteBottomSheet,
    selectedTab,
    setSelectedTab,
    employeeMatricola,
    employeeFiliale,
    setRestoreEmployeeConfirmationOpen,
    restoreEmployeeConfirmationOpen,
    onRestoreEmployee,
  } = useAttendanceMobileEmployeeScreen();

  if (!attendance) {
    return <FullScreenSpinner />;
  }

  return (
    <>
      <TopBarMobile headerString={translate("attendances", lang)} />

      <div className="relative p-6 flex flex-col gap-4 h-[calc(100%-4rem)] overflow-hidden">
        <div className="absolute top-0 left-0 p-6 w-full bg-canvas-primary">
          <AttendancesMobileHeader
            primaryText={attendance.employee.name}
            secondaryText={`${translate(`month${month - 1}` as TranslatorKeys, lang)} ${year}`}
            rightSide={
              <button
                onClick={() => setRestoreEmployeeConfirmationOpen(true)}
                className="p-2"
              >
                <FontAwesomeIcon
                  icon={faArrowTurnLeft}
                  className="text-primary text-lg"
                />
              </button>
            }
          />
        </div>

        <div className="absolute top-20 left-0 p-6 h-[calc(100vh-12rem)] overflow-scroll">
          {attendance.note.trim() === "" ? (
            <PressableDashedBox icon={faPlus} onClick={openNoteBottomSheet}>
              {translate("addNote", lang)}
            </PressableDashedBox>
          ) : (
            <div className="p-2 bg-white rounded-md border-neutral border flow-root">
              <button className="float-right p-2" onClick={openNoteBottomSheet}>
                <FontAwesomeIcon
                  icon={faPencil}
                  className="text-primary text-lg"
                />
              </button>

              <span
                ref={noteRef}
                className={clsx(
                  !noteIsExpanded && "line-clamp-2",
                  "text-base",
                  "text-font-secondary",
                )}
              >
                {attendance.note.trim()}
              </span>

              {noteIsClamped && !noteIsExpanded && (
                <button
                  className="text-primary font-medium text-base"
                  onClick={() => setNoteIsExpanded(true)}
                >
                  {translate("showMore", lang)}
                </button>
              )}
            </div>
          )}

          <div className="sticky -top-7 mb-4 z-1 flex justify-center items-center">
            <TabList
              size="large"
              className="w-[100vw] -mx-6 bg-canvas-primary flex flex-row justify-center"
              defaultSelectedValue={
                "hours" as AttendanceMobileEmployeeScreenTab
              }
              onTabSelect={(_, { value }) =>
                setSelectedTab(value as AttendanceMobileEmployeeScreenTab)
              }
            >
              <Tab value={"hours" as AttendanceMobileEmployeeScreenTab}>
                <div className="w-[calc(calc(100vw-4rem)/2)]">
                  {translate("hours", lang)}
                </div>
              </Tab>
              <Tab value={"voci" as AttendanceMobileEmployeeScreenTab}>
                <div className="w-[calc(calc(100vw-4rem)/2)] flex flex-row justify-center">
                  <div className="flex flex-row gap-2 items-center">
                    <div>{translate("voci", lang)}</div>
                    <div
                      className={clsx(
                        "bg-primary-50",
                        "rounded-full",
                        "px-2.5",
                        "flex",
                        "text-sm",
                        "text-primary",
                      )}
                    >
                      <div className="">{attendance.voci.length}</div>
                    </div>
                  </div>
                </div>
              </Tab>
            </TabList>
          </div>

          {selectedTab === "hours" ? (
            <AttendanceMobileEmployeeHoursTab
              month={month}
              year={year}
              attendance={attendance}
            />
          ) : (
            selectedTab === "voci" && (
              <AttendanceMobileEmployeeVociTab
                employeeMatricola={employeeMatricola}
                employeeFiliale={employeeFiliale}
              />
            )
          )}
        </div>
      </div>
      <MobileBottomSheet
        isOpen={editNoteBottomSheetOpen}
        onClose={closeNoteBottomSheet}
        title={translate("note", lang)}
      >
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <div className="text-font-secondary text-base">
              {attendance.employee.name} |{" "}
              {translate(`month${month - 1}` as TranslatorKeys, lang)} {year}
            </div>
            <PspTextArea
              label="note"
              value={note}
              onChange={setNote}
              rows={8}
            />
            <div className="text-font-secondary">
              {translate("attendanceNoteMessage", lang, {
                "{employeeName}": attendance.employee.name,
                "{month}": translate(
                  `month${month - 1}` as TranslatorKeys,
                  lang,
                ),
                "{year}": year.toString(),
              })}
            </div>
          </div>
          <PspButton onClick={onSaveNote} appearance="primary">
            {translate("save", lang)}
          </PspButton>
        </div>
      </MobileBottomSheet>
      <ConfirmationDialog
        titleString={translate("discardAllChangesAttendancesTitle", lang)}
        messageString={translate("discardAllChangesAttendancesBody", lang, {
          "{employeeName}": attendance.employee.name,
        })}
        isOpen={restoreEmployeeConfirmationOpen}
        onCancel={() => setRestoreEmployeeConfirmationOpen(false)}
        onConfirm={onRestoreEmployee}
      />
    </>
  );
}
